import { MAIN_CONTENT_ID } from '@shared/constants/ContentIdValues';
import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';

export default function useOrgProductsSearch() {
    const { t, i18n } = useTranslation();
    const { organizationId } = useParams<{ organizationId: string }>();
    const { showError } = AlertsContainer.useContainer();
    const organizationContainer = OrganizationContainer.useContainer();
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });

    useScrollToElement(MAIN_CONTENT_ID, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(
        () => organizationContainer.orgProducts.length >= displayLimit,
        [organizationContainer.orgProducts]
    );

    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleNext = () => {
        const count = organizationContainer.orgManagedKeysLicenseRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const count = organizationContainer.orgProductsRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (organizationContainer.loadingDataRef.current.products || !organizationId) return;
            try {
                organizationContainer.handleLoadingDataChange('products', true);
                organizationContainer.getOrganizationProducts(organizationId, i18n.language, debouncedQuery);
            } catch (e) {
                showError(t('common:license.key.product.error.title'));
            } finally {
                organizationContainer.handleLoadingDataChange('products', false);
            }
        };
        trySearch();
    }, [debouncedQuery]);

    return {
        results: organizationContainer.orgProducts,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        loadingData: organizationContainer.loadingData.licenseKeys,
        displayLimit,
    };
}
